import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { ShieldIcon, LockIcon, BoltIcon, DocIcon, DownloadIcon, SearchIcon, CodeIcon, CloudIcon, MobileIcon } from './Icons';

export function downloadTechDossierPdf() {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(22);
  doc.setTextColor(15, 23, 42);
  doc.text('BRAMHAWARE TECHNOLOGIES', 20, 22);

  doc.setFontSize(9);
  doc.setTextColor(37, 99, 235);
  doc.text('CUSTOM SOFTWARE DEVELOPMENT & TECHNICAL SEO COMPANY // SPECIFICATIONS', 20, 28);

  doc.setDrawColor(203, 213, 225);
  doc.line(20, 33, 190, 33);

  // Section 1: Overview & Services
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('1. Full-Cycle Software Engineering Services', 20, 42);

  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text(
    'Bramhaware Technologies (Pune, India) engineers high-performance web applications,\nenterprise platforms, mobile apps, and technical SEO architectures for startups and global enterprises.',
    20,
    48
  );

  doc.setFontSize(8.5);
  doc.setTextColor(30, 41, 59);
  doc.text('• Custom Web Applications: React, Next.js, Node.js, Spring Boot microservices.', 25, 62);
  doc.text('• Technical SEO & Growth: Sub-second Core Web Vitals, Schema.org graphs, edge SSR & AEO.', 25, 68);
  doc.text('• Mobile App Engineering: Cross-platform iOS & Android mobile apps via Flutter & React Native.', 25, 74);
  doc.text('• Cloud & DevOps: AWS/Azure architecture, Kubernetes, Docker, CI/CD automated pipelines.', 25, 80);
  doc.text('• Enterprise SaaS & Systems: Multi-tenant billing, GST tax ledgers, biometric telemetry.', 25, 86);

  // Section 2: Technical SEO Engine
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('2. Technical SEO & Search Dominance Engine', 20, 98);

  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Core Web Vitals Engineering: LCP < 1.2s, CLS < 0.05, INP < 150ms for top ranking scores.', 25, 105);
  doc.text('• Semantic Graph Architecture: Deep Schema.org structured data (Organization, Software, Article).', 25, 111);
  doc.text('• Edge Pre-Rendering (SSR): Dynamic rendering for search engine bots, delivering instant indexation.', 25, 117);
  doc.text('• Generative Search Optimization (AEO): Optimized for AI answer engines (Perplexity, ChatGPT, Gemini).', 25, 123);

  // Section 3: Architecture
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('3. Distributed Three-Tier Cloud Architecture', 20, 135);

  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Tier 1 (Edge & Gateway): CloudFront CDN, SSL/TLS 1.3, DDoS Rate-Limiting & JWT routing.', 25, 142);
  doc.text('• Tier 2 (Logic Backplane): Spring Boot microservices, Kafka event streams, horizontal autoscaling.', 25, 148);
  doc.text('• Tier 3 (Persistence): Partitioned PostgreSQL instances, Redis cache, AES-256 automated backups.', 25, 154);

  // Section 4: Case Studies
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('4. Proven In-House Platforms & Case Studies', 20, 166);

  doc.setFontSize(8.5);
  doc.setTextColor(30, 41, 59);
  doc.text('• The Foundora (thefoundora.com): National Startup & Investor Network indexing 2,400+ ventures across 28 states.', 25, 173);
  doc.text('• InvoiceGen: 3D Smart GST Invoicing & Automated Tax Ledger Platform.', 25, 179);
  doc.text('• AttendMax: Biometric Workforce Telemetry & Personnel Scheduling Engine.', 25, 185);
  doc.text('• NewsApp: Distributed Digital Newsroom Engine built with Spring Boot microservices.', 25, 191);

  // Section 5: Security & Compliance
  doc.setFontSize(11);
  doc.setTextColor(15, 23, 42);
  doc.text('5. Security, Privacy & DPDP Compliance', 20, 203);

  doc.setFontSize(8.5);
  doc.setTextColor(71, 85, 105);
  doc.text('• Zero-Trust Network Architecture with tenant database row-level security isolation.', 25, 210);
  doc.text('• Full conformance with Indian Digital Personal Data Protection (DPDP) and GDPR standards.', 25, 216);
  doc.text('• Sub-second multi-AZ failover, automated point-in-time recovery, and tamper-evident audit logs.', 25, 222);

  // Footer
  doc.setDrawColor(203, 213, 225);
  doc.line(20, 240, 190, 240);
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(
    'Bramhaware Technologies Pvt. Ltd. • Pune, Maharashtra, India • info@bramhaware.in • https://bramhaware.in',
    20,
    248
  );

  doc.save('Bramhaware_Technologies_Software_SEO_Brief.pdf');
}

export function TechDossierModal({ isOpen, onClose, onNavigateToContact }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState(false);
  const totalPages = 5;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrentPage((p) => Math.min(totalPages, p + 1));
      if (e.key === 'ArrowLeft') setCurrentPage((p) => Math.max(1, p - 1));
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleDownloadPDF = () => {
    setIsDownloading(true);
    try {
      downloadTechDossierPdf();
    } catch (err) {
      console.error('PDF Generation Error:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dossier-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="dossier-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <header className="dossier-modal-header">
          <div className="dossier-header-brand">
            <span className="dossier-tag">ENGINEERING SPECIFICATION // SERVICES &amp; ARCHITECTURE</span>
            <h3>Bramhaware Capabilities &amp; System Blueprint</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              type="button"
              className="dossier-pdf-btn"
              onClick={handleDownloadPDF}
              title="Download Branded Executive Brief (PDF)"
            >
              <DownloadIcon className="dossier-btn-icon" />
              <span>{isDownloading ? 'Generating...' : 'Download PDF'}</span>
            </button>
            <button
              type="button"
              className="dossier-close-btn"
              onClick={onClose}
              aria-label="Close Blueprint Modal"
            >
              ✕
            </button>
          </div>
        </header>

        {/* Modal Pagination Navigation Tabs */}
        <nav className="dossier-tabs-row">
          {[
            { page: 1, title: '01. Software Services' },
            { page: 2, title: '02. Cloud Architecture' },
            { page: 3, title: '03. Technical SEO Engine' },
            { page: 4, title: '04. Platform Case Studies' },
            { page: 5, title: '05. Security & DPDP' },
          ].map((t) => (
            <button
              key={t.page}
              type="button"
              className={`dossier-tab-btn ${currentPage === t.page ? 'active' : ''}`}
              onClick={() => setCurrentPage(t.page)}
            >
              {t.title}
            </button>
          ))}
        </nav>

        {/* Modal Scrollable Body */}
        <div className="dossier-modal-scrollable">
          {/* TAB 1: SOFTWARE DEVELOPMENT SERVICES */}
          {currentPage === 1 && (
            <div className="dossier-sheet-content">
              <div className="sheet-badge-row">
                <span className="sheet-pill">CUSTOM SOFTWARE &amp; CLOUD ENGINEERING</span>
                <span className="sheet-pill-outline">PUNE, MAHARASHTRA, INDIA</span>
              </div>
              <h4 className="sheet-title">Full-Cycle Software Engineering Services</h4>
              <p className="sheet-lead">
                Bramhaware Technologies is a dedicated software development studio. We build production-grade web applications, enterprise platforms, mobile applications, and technical search architectures designed for permanence and speed.
              </p>

              <div className="sheet-metrics-grid">
                <div className="sheet-metric-card">
                  <span className="metric-val">100+</span>
                  <span className="metric-lbl">Deployments Shipped</span>
                </div>
                <div className="sheet-metric-card">
                  <span className="metric-val">99.9%</span>
                  <span className="metric-lbl">Cloud Uptime SLA</span>
                </div>
                <div className="sheet-metric-card">
                  <span className="metric-val">&lt; 1.2s</span>
                  <span className="metric-lbl">Core Web Vitals LCP</span>
                </div>
                <div className="sheet-metric-card">
                  <span className="metric-val">2,400+</span>
                  <span className="metric-lbl">Ventures Scaled</span>
                </div>
              </div>

              <div className="sheet-quote-box">
                <p>&ldquo;We don&apos;t just code; we engineer software platforms that give our clients sustainable market advantage and organic search rank dominance.&rdquo;</p>
              </div>
            </div>
          )}

          {/* TAB 2: THREE-TIER CLOUD ARCHITECTURE */}
          {currentPage === 2 && (
            <div className="dossier-sheet-content">
              <div className="sheet-badge-row">
                <span className="sheet-pill">DECOUPLED CLOUD BACKPLANE</span>
                <span className="sheet-pill-outline">HIGH-CONCURRENCY MESH</span>
              </div>
              <h4 className="sheet-title">Three-Tier Decoupled Architecture</h4>
              <p className="sheet-lead">
                Our platforms are constructed on a clean decoupled topology designed for 99.99% availability, elastic autoscaling, and millisecond edge latency.
              </p>

              <div className="sheet-stack-diagram">
                <div className="sheet-stack-tier">
                  <div className="tier-num">TIER 1</div>
                  <div className="tier-details">
                    <strong>Client Edge &amp; Dynamic Pre-Rendering Layer</strong>
                    <p>CloudFront CDN, automated DDoS shielding, SSL/TLS 1.3 termination, edge SSR for search bots, and tokenized session routing.</p>
                    <div className="tier-tags">
                      <span>CloudFront</span>
                      <span>Next.js / SSR</span>
                      <span>Rate Limiter</span>
                      <span>Core Web Vitals</span>
                    </div>
                  </div>
                </div>

                <div className="sheet-stack-tier">
                  <div className="tier-num">TIER 2</div>
                  <div className="tier-details">
                    <strong>Spring Boot &amp; Event Choreography Backplane</strong>
                    <p>Containerized Spring Cloud microservices communicating via Kafka event streams and async worker pools.</p>
                    <div className="tier-tags">
                      <span>Spring Boot</span>
                      <span>Docker / K8s</span>
                      <span>Apache Kafka</span>
                      <span>Node.js</span>
                    </div>
                  </div>
                </div>

                <div className="sheet-stack-tier">
                  <div className="tier-num">TIER 3</div>
                  <div className="tier-details">
                    <strong>Multi-Tenant Vault &amp; Persistence</strong>
                    <p>Partitioned PostgreSQL instances with Row-Level Security (RLS), Redis caching, and AES-256 automated backups.</p>
                    <div className="tier-tags">
                      <span>PostgreSQL</span>
                      <span>Redis 7</span>
                      <span>AES-256 Vault</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: TECHNICAL SEO & GROWTH ENGINEERING */}
          {currentPage === 3 && (
            <div className="dossier-sheet-content">
              <div className="sheet-badge-row">
                <span className="sheet-pill">ORGANIC SEARCH DOMINANCE</span>
                <span className="sheet-pill-outline">CORE WEB VITALS &bull; SCHEMA.ORG</span>
              </div>
              <h4 className="sheet-title">Technical SEO &amp; Search Architecture</h4>
              <p className="sheet-lead">
                SEO is not an afterthought at Bramhaware; it is engineered directly into the software source code. We optimize site architecture so Google and AI search engines index and rank client platforms at the highest tiers.
              </p>

              <div className="sheet-security-grid">
                <div className="security-point-card">
                  <h5><BoltIcon className="sheet-point-icon" /> Sub-Second Core Web Vitals</h5>
                  <p>Guaranteed green scores: Largest Contentful Paint (LCP &lt; 1.2s), Cumulative Layout Shift (CLS &lt; 0.05), and Interaction to Next Paint (INP &lt; 150ms).</p>
                </div>
                <div className="security-point-card">
                  <h5><DocIcon className="sheet-point-icon" /> Schema.org Semantic Graphs</h5>
                  <p>Comprehensive structured data graphs (Organization, SoftwareApplication, Product, FAQ, Article) injected natively for rich Google search snippets.</p>
                </div>
                <div className="security-point-card">
                  <h5><CloudIcon className="sheet-point-icon" /> Server-Side Edge Rendering (SSR)</h5>
                  <p>Dynamic pre-rendering delivers complete, hydration-ready HTML payloads to Googlebot and crawler agents with zero JavaScript execution delays.</p>
                </div>
                <div className="security-point-card">
                  <h5><SearchIcon className="sheet-point-icon" /> Generative Search Optimization (AEO)</h5>
                  <p>Engineered for next-generation Answer Engine Optimization, ensuring your company is directly cited by ChatGPT, Perplexity, and Google Gemini.</p>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: PROVEN CASE STUDIES & LIVE SOFTWARE */}
          {currentPage === 4 && (
            <div className="dossier-sheet-content">
              <div className="sheet-badge-row">
                <span className="sheet-pill">PROVEN ENGINEERING</span>
                <span className="sheet-pill-outline">LIVE PRODUCTION PORTFOLIO</span>
              </div>
              <h4 className="sheet-title">Case Studies: Software Built by Bramhaware</h4>
              <p className="sheet-lead">
                We prove our capabilities through live software deployed in production across India:
              </p>

              <div className="sheet-venture-list">
                <div className="sheet-venture-item">
                  <div className="venture-item-header">
                    <strong>01. The Foundora</strong>
                    <span className="venture-status-chip">National Network</span>
                  </div>
                  <p>Startup &amp; investor intelligence platform cataloging 2,400+ ventures across 28 states with real-time matching algorithms.</p>
                  <a href="https://thefoundora.com" target="_blank" rel="noreferrer" className="dossier-venture-link">thefoundora.com ↗</a>
                </div>

                <div className="sheet-venture-item">
                  <div className="venture-item-header">
                    <strong>02. InvoiceGen</strong>
                    <span className="venture-status-chip">Enterprise SaaS</span>
                  </div>
                  <p>Smart 3D GST billing platform generating audit-ready PDF/HTML invoices with real-time tax analytics.</p>
                  <Link to="/invoicegen" className="dossier-venture-link" onClick={onClose}>Launch InvoiceGen ↗</Link>
                </div>

                <div className="sheet-venture-item">
                  <div className="venture-item-header">
                    <strong>03. AttendMax</strong>
                    <span className="venture-status-chip">Biometric Telemetry</span>
                  </div>
                  <p>Workforce tracking and role-based shift orchestration with biometric attendance telemetry.</p>
                  <a href="/AttendMax_Presentation.html" target="_blank" rel="noreferrer" className="dossier-venture-link">View AttendMax ↗</a>
                </div>

                <div className="sheet-venture-item">
                  <div className="venture-item-header">
                    <strong>04. NewsApp Publishing</strong>
                    <span className="venture-status-chip">Digital Media</span>
                  </div>
                  <p>High-velocity distributed newsroom publishing engine built with Spring Cloud microservices.</p>
                  <a href="/NewsApp-Presentation.html" target="_blank" rel="noreferrer" className="dossier-venture-link">Explore NewsApp ↗</a>
                </div>

                <div className="sheet-venture-item">
                  <div className="venture-item-header">
                    <strong>05. Brilliant Share Market Academy</strong>
                    <span className="venture-status-chip">FinTech 3D EdTech</span>
                  </div>
                  <p>Interactive 3D trading terminal, real-time market dashboard, and position sizing risk calculator for Pune trading academy.</p>
                  <Link to="/brilliantsharemarket" className="dossier-venture-link" onClick={onClose}>Explore 3D Trading Portal ↗</Link>
                </div>
              </div>
            </div>
          )}

          {/* TAB 5: SECURITY & COMPLIANCE */}
          {currentPage === 5 && (
            <div className="dossier-sheet-content">
              <div className="sheet-badge-row">
                <span className="sheet-pill">ZERO-TRUST SECURITY</span>
                <span className="sheet-pill-outline">INDIAN DPDP &amp; GDPR READY</span>
              </div>
              <h4 className="sheet-title">Security, Privacy &amp; Data Protection</h4>
              <p className="sheet-lead">
                Every software application engineered at Bramhaware conforms to strict enterprise security standards, tenant isolation, and modern Indian Digital Personal Data Protection (DPDP) principles.
              </p>

              <div className="sheet-security-grid">
                <div className="security-point-card">
                  <h5><ShieldIcon className="sheet-point-icon" /> Cryptographic Tenant Isolation</h5>
                  <p>Row-level security and isolated tenancy keys ensure absolute separation between client datasets.</p>
                </div>
                <div className="security-point-card">
                  <h5><LockIcon className="sheet-point-icon" /> End-to-End Encryption</h5>
                  <p>Data secured via TLS 1.3 in transit and AES-256 at rest across all database clusters and storage volumes.</p>
                </div>
                <div className="security-point-card">
                  <h5><BoltIcon className="sheet-point-icon" /> High-Availability Failover</h5>
                  <p>Automated health checks, multi-AZ cloud redundancy, and continuous point-in-time recovery mitigate downtime.</p>
                </div>
                <div className="security-point-card">
                  <h5><DocIcon className="sheet-point-icon" /> Tamper-Proof Audit Trails</h5>
                  <p>Comprehensive event logging across administrative operations and critical transaction conduits.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer Controls */}
        <footer className="dossier-modal-footer">
          <div className="dossier-pagination-ctrls">
            <button
              type="button"
              className="dossier-nav-arrow-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              ← Previous
            </button>
            <span className="dossier-page-counter">
              Page {currentPage} of {totalPages}
            </span>
            <button
              type="button"
              className="dossier-nav-arrow-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Next →
            </button>
          </div>

          <button
            type="button"
            className="dossier-cta-btn"
            onClick={() => {
              onClose();
              if (onNavigateToContact) onNavigateToContact();
            }}
          >
            Hire Us / Start Project →
          </button>
        </footer>
      </div>
    </div>
  );
}
